import React from "react";
import { motion } from "framer-motion";
import { MapPin, BadgeCheck, Database, Building2, Calculator, LineChart, ShieldCheck, Users, FileCheck, HandCoins } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useLang } from "@/context/LanguageContext";

/*
  About page, rewritten Sept 2026.
  Positioning: independent Dubai real estate intelligence platform whose
  transaction execution is handled by a RERA licensed partner broker
  (Provest Real Estate LLC, RERA 43950, DED License / ORN 1380543).

  Visual rules enforced by owner:
    1. Container spans full max width (matches site header edge to edge).
    2. Zero em dashes or standalone hyphens as separators in visible copy.
       Compound words rewritten as spaces where legible.
    3. Two step journey shown as two stacked cards, both light theme.
    4. Arabic mode is fully Arabic, no Latin words. License numbers use
       Eastern Arabic Indic digits.
*/
export default function About() {
  const { lang, lh } = useLang();
  const isAR = lang === "ar";

  // ------ SEO meta ------
  const seoTitle = isAR
    ? "عن بروبيرتك من بروفست | استشارات عقار دبي"
    : "About Propertic by Provest | Dubai Real Estate Advisory";
  const seoDesc = isAR
    ? "تعرّف على بروبيرتك من بروفست، منصّة استشارات عقارية في دبي مرخّصة من ريرا تُقدّم بيانات السوق، أدوات العقار، وإرشاد المستثمرين."
    : "Learn about Propertic by Provest, a RERA-licensed Dubai real estate advisory platform providing market data, property tools and investor guidance.";
  const canonical = isAR
    ? "https://thepropertic.ae/about?lang=ar"
    : "https://thepropertic.ae/about";

  // ------ JSON-LD @graph ------
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["Organization", "RealEstateAgent", "LocalBusiness"],
        "@id": "https://thepropertic.ae/#organization",
        "name": "The Propertic",
        "alternateName": ["The Propertic by Provest", "Propertic"],
        "legalName": "Provest Real Estate L.L.C",
        "url": "https://thepropertic.ae",
        "logo": { "@type": "ImageObject", "url": "https://thepropertic.ae/logo.png", "width": 512, "height": 512 },
        "image": "https://thepropertic.ae/og-share-logo.png",
        "description": isAR
          ? "منصّة تحليل عقارات دبي تعتمد البيانات أولاً، تابعة لوسيط عقاري مرخّص من ريرا (بروفست العقارية ذ.م.م، ريرا رقم ٤٣٩٥٠، رخصة دائرة الاقتصاد ورقم تسجيل المكتب ١٣٨٠٥٤٣)."
          : "Data first Dubai real estate intelligence platform, operated by a RERA licensed brokerage (Provest Real Estate L.L.C, RERA 43950, DED License / ORN 1380543).",
        "identifier": [
          { "@type": "PropertyValue", "name": "RERA Registration", "value": "43950" },
          { "@type": "PropertyValue", "name": "DED License / Office Registration Number", "value": "1380543" }
        ],
        "hasCredential": [
          {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "license",
            "name": "RERA Broker Registration",
            "identifier": "43950",
            "recognizedBy": { "@type": "GovernmentOrganization", "name": "Real Estate Regulatory Agency (RERA), Dubai Land Department", "url": "https://dubailand.gov.ae" }
          },
          {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "license",
            "name": "DED Commercial License / Office Registration Number (ORN)",
            "identifier": "1380543",
            "recognizedBy": { "@type": "GovernmentOrganization", "name": "Dubai Department of Economy & Tourism", "url": "https://ded.ae" }
          }
        ],
        "address": { "@type": "PostalAddress", "streetAddress": "Al Barsha 1", "addressLocality": "Dubai", "addressCountry": "AE" },
        "areaServed": [
          { "@type": "Country", "name": "United Arab Emirates" },
          { "@type": "City", "name": "Dubai" }
        ],
        "knowsAbout": [
          "Dubai off plan property",
          "Dubai Land Department open data",
          "RERA compliance and due diligence",
          "Dubai real estate ROI and yield analysis",
          "UAE Golden Visa investor requirements",
          "Central Bank of the UAE mortgage rates",
          "Dubai freehold zones",
          "Escrow account verification",
          "Sales and Purchase Agreement review",
          "DLD Real Estate Registration Trustee process"
        ],
        "sameAs": [
          "https://dubailand.gov.ae/en/eservices/rera-permit-verification/"
        ],
        "founder": { "@type": "Organization", "name": "Provest Real Estate L.L.C" }
      },
      {
        "@type": "AboutPage",
        "@id": "https://thepropertic.ae/about#aboutpage",
        "url": canonical,
        "inLanguage": isAR ? "ar" : "en",
        "name": seoTitle,
        "description": seoDesc,
        "isPartOf": { "@id": "https://thepropertic.ae/#website" },
        "mainEntity": { "@id": "https://thepropertic.ae/#organization" },
        "primaryImageOfPage": { "@type": "ImageObject", "url": "https://thepropertic.ae/og-share-logo.png" }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": isAR ? "الرئيسية" : "Home", "item": "https://thepropertic.ae/" },
          { "@type": "ListItem", "position": 2, "name": isAR ? "من نحن" : "About", "item": "https://thepropertic.ae/about" }
        ]
      },
      {
        "@type": "Service",
        "@id": "https://thepropertic.ae/about#service",
        "serviceType": isAR ? "استشارات وتحليلات عقارية في دبي" : "Dubai real estate intelligence and licensed advisory",
        "provider": { "@id": "https://thepropertic.ae/#organization" },
        "areaServed": { "@type": "City", "name": "Dubai" },
        "audience": { "@type": "Audience", "audienceType": isAR ? "مستثمرو العقارات الدوليون" : "International real estate investors" },
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "AED",
          "description": isAR ? "أدوات بيانات دائرة الأراضي والأملاك مجاناً وبدون تسجيل" : "Free open access DLD data tools"
        }
      },
      {
        "@type": "FAQPage",
        "@id": "https://thepropertic.ae/about#faq",
        "mainEntity": [
          {
            "@type": "Question",
            "name": isAR ? "هل بروبيرتك وسيط عقاري مرخّص؟" : "Is The Propertic a licensed real estate broker?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": isAR
                ? "بروبيرتك هي منصّة تحليل عقاري تابعة لشركة بروفست العقارية ذ.م.م، وهي وسيط عقاري مرخّص من مؤسّسة التنظيم العقاري (ريرا) في دبي برقم ريرا ٤٣٩٥٠، ومسجّلة لدى دائرة الاقتصاد والسياحة برخصة ورقم تسجيل مكتب ١٣٨٠٥٤٣. جميع الاستشارات والصفقات تُنفَّذ حصراً عبر بروفست."
                : "The Propertic is the intelligence platform of Provest Real Estate L.L.C, a Dubai brokerage licensed by the Real Estate Regulatory Agency (RERA) under RERA registration number 43950, and registered with the Department of Economy and Tourism under license and Office Registration Number (ORN) 1380543. Every consultation and transaction is executed exclusively through Provest."
            }
          },
          {
            "@type": "Question",
            "name": isAR ? "هل استخدام أدوات المنصّة مجاني وبدون تسجيل؟" : "Are the data tools free and open access?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": isAR
                ? "نعم. جميع أدواتنا (تحليلات دائرة الأراضي والأملاك الأسبوعية، ومقارنة المناطق، ومحلّل الصفقات، وحاسبة الرهن العقاري، ومؤشّرات العائد) متاحة مجاناً وبدون تسجيل أو مشاركة بيانات اتصال. نطلب معلوماتك فقط عند طلبك استشارة رسمية مع مستشار مرخّص."
                : "Yes. Every tool (weekly DLD insights, area comparison, deal analyzer, mortgage calculator, yield indicators) is free with no signup and no contact details required. We only ask for your information when you explicitly request a consultation with a licensed advisor."
            }
          },
          {
            "@type": "Question",
            "name": isAR ? "ما مصادر البيانات التي تعتمدون عليها؟" : "What data sources do you use?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": isAR
                ? "بيانات المبيعات والأسعار مصدرها بوابة البيانات المفتوحة لدائرة الأراضي والأملاك، وأسعار الفائدة من المصرف المركزي الإماراتي. أما الإيجارات والعوائد فهي تقديرات من بروبيرتك، وتُعرض المنهجية بجانب كل رقم."
                : "Sales and price data come from the Dubai Land Department Open Data Gateway, and interest rates from the Central Bank of the UAE. Rents and yields are Propertic estimates, with the method shown beside each figure."
            }
          },
          {
            "@type": "Question",
            "name": isAR ? "من يُدير تسجيل الملكية في دائرة الأراضي والأملاك؟" : "Who handles the DLD title transfer?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": isAR
                ? "تسجيل نقل الملكية النهائي تُنفّذه مكاتب التسجيل العقاري المعتمدة التابعة لدائرة الأراضي والأملاك في دبي. دور مستشارينا المرخّصين هو مرافقتكم عبر مراحل اتفاقية البيع، من الحجز حتى استلام المفاتيح."
                : "Final title transfers are executed by DLD Real Estate Registration Trustees, the government authorized offices operating under the Dubai Land Department. Our licensed advisors support you across the Sales and Purchase Agreement journey, from booking to key handover."
            }
          }
        ]
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href="https://thepropertic.ae/about" />
        <link rel="alternate" hrefLang="ar" href="https://thepropertic.ae/about?lang=ar" />
        <link rel="alternate" hrefLang="x-default" href="https://thepropertic.ae/about" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="https://thepropertic.ae/og-share-logo.png" />
        <meta property="og:locale" content={isAR ? "ar_AE" : "en_AE"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="section relative overflow-hidden" data-testid="about-page">
        <div className="blob-gold w-[500px] h-[500px] -top-20 -end-40" />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative">
          {/* ============ HERO ============ */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-mono-brand text-propertic-gold mb-4 text-[11px] tracking-[0.22em] uppercase"
            data-testid="about-eyebrow"
          >
            {isAR ? "من نحن · منصّة تحليل تعتمد البيانات أولاً" : "About, Data First Intelligence Platform"}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="font-editorial text-3xl sm:text-4xl lg:text-5xl text-propertic-ink leading-[1.12]"
            data-testid="about-title"
          >
            {isAR ? (
              <>تحليلات موضوعية للسوق أولاً. <span className="font-italic-serif text-propertic-gold">ومستشارون مرخّصون من ريرا متى كنتم مستعدّين.</span></>
            ) : (
              <>Objective market intelligence first. <span className="font-italic-serif text-propertic-gold">RERA licensed advisors when you are ready.</span></>
            )}
          </motion.h1>

          <p className="mt-8 text-propertic-ink-2/95 text-lg leading-[1.75]">
            {isAR
              ? "لا يجب أن تبدأ رحلتكم في سوق دبي العقاري بمكالمات بيع عدوانية أو ادّعاءات مطوّرين غير موثّقة. بُنيت بروبيرتك كمنصّة تحليل عقاري تعتمد البيانات أولاً، تقوم على مبدأ واحد: يستحقّ المستثمرون شفافية كاملة في البيانات قبل التحدّث إلى أي مستشار."
              : "Navigating Dubai's fast paced property market should not start with aggressive sales calls or unverified developer hype. The Propertic was built as a data first real estate intelligence platform designed around a single premise: investors deserve total data transparency before they speak to an advisor."}
          </p>
          <p className="mt-5 text-propertic-ink-2/90 text-base leading-[1.75]">
            {isAR
              ? "نُغلق الفجوة بين التحليل الذاتي للسوق وبين التنفيذ المرخّص. بصفتها منصّة تحليل عقاري تعتمد البيانات أولاً وتُقدّم أدوات حيّة ومفتوحة دون تسجيل، نُتيح للمستثمرين حول العالم اختبار السيناريوهات وتحليل الأرقام الحقيقية وبناء يقين استثماري كامل قبل التحدّث مع مستشارينا المرخّصين من ريرا. بروبيرتك هي منصّة الأدوات المجانية التابعة لشركة بروفست العقارية ذ.م.م، الوسيط العقاري المرخّص من ريرا (رقم ٤٣٩٥٠)، وقد نستفيد تجارياً من المعاملات الناتجة عن خدماتنا الاستشارية."
              : "We bridge the gap between self-service market analysis and licensed deal execution. As a data first intelligence platform providing open access, live market tools with no sign-up required, we let global investors test scenarios, analyze real numbers, and build total investment clarity on their own terms, before speaking with our RERA licensed advisors. The Propertic is the free online tools platform of Provest Real Estate L.L.C, a RERA licensed brokerage (RERA 43950), and we may commercially benefit from transactions resulting from our advisory services."}
          </p>

          {/* ============ VERIFIED CARDS ============ */}
          <div className="mt-12 grid md:grid-cols-3 gap-4" data-testid="about-verified-cards">
            <VerifiedCard
              icon={<BadgeCheck className="w-5 h-5" />}
              label={isAR ? "رقم ريرا" : "RERA registration"}
              value={isAR ? "ريرا ٤٣٩٥٠" : "RERA 43950"}
              note={isAR ? "مؤسّسة التنظيم العقاري · دائرة الأراضي والأملاك" : "Real Estate Regulatory Agency · Dubai Land Department"}
              href="https://dubailand.gov.ae"
              testid="card-rera"
            />
            <VerifiedCard
              icon={<BadgeCheck className="w-5 h-5" />}
              label={isAR ? "رخصة دائرة الاقتصاد · رقم تسجيل المكتب" : "DED License · ORN"}
              value={isAR ? "١٣٨٠٥٤٣" : "1380543"}
              note={isAR ? "بروفست العقارية ذ.م.م · دائرة الاقتصاد والسياحة" : "Provest Real Estate L.L.C · Dept. of Economy & Tourism"}
              testid="card-legal"
            />
            <VerifiedCard
              icon={<MapPin className="w-5 h-5" />}
              label={isAR ? "المقر" : "Office"}
              value={isAR ? "البرشاء الأولى" : "Al Barsha 1"}
              note={isAR ? "دبي، الإمارات العربية المتحدة" : "Dubai, United Arab Emirates"}
              testid="card-office"
            />
          </div>

          {/* ============ SECTION: TWO STEP JOURNEY (STACKED) ============ */}
          <div className="mt-20" data-testid="about-two-phase">
            <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-3">
              {isAR ? "كيف تعمل بروبيرتك" : "How The Propertic works"}
            </p>
            <h2 className="font-editorial text-2xl sm:text-3xl text-propertic-navy leading-tight">
              {isAR ? (
                <>رحلة من <span className="font-italic-serif text-propertic-gold">مرحلتين</span>: أدوات وبيانات للاستخدام الذاتي، ثمّ إرشاد مرخّص.</>
              ) : (
                <>A <span className="font-italic-serif text-propertic-gold">two step</span> journey: self service data, then licensed guidance.</>
              )}
            </h2>

            <div className="mt-8 space-y-5 lg:space-y-6">
              {/* PHASE 1, LIGHT */}
              <div className="rounded-[1.5rem] border hairline bg-white/80 p-6 sm:p-8 relative" data-testid="phase-1">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-50 text-emerald-700 px-3 py-1 mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                  <span className="font-mono-brand text-[10px] tracking-[0.2em] uppercase">
                    {isAR ? "المرحلة الأولى · المنصّة" : "Phase 1, The Platform"}
                  </span>
                </div>
                <h3 className="font-editorial text-xl sm:text-2xl text-propertic-navy mb-3">
                  {isAR ? "أدوات وبيانات للتحليل الذاتي والتحقّق من الصفقات" : "Self-service tools and data for deal verification"}
                </h3>
                <p className="text-[14.5px] text-propertic-ink-2/90 leading-relaxed mb-6">
                  {isAR
                    ? "قبل طلب أي استشارة، يستخدم المستثمرون أدواتنا المجانية لتقييم السوق بموضوعية:"
                    : "Before requesting any consultation, investors leverage our free tools to evaluate the market objectively:"}
                </p>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-5 text-[13.5px] text-propertic-ink leading-relaxed">
                  <BulletItem
                    icon={<Database className="w-4 h-4 text-propertic-gold" />}
                    title={isAR ? "تحليلات السوق الأسبوعية" : "Weekly market intelligence"}
                    body={isAR
                      ? "تتبّعوا المبيعات المسجّلة وأسعار القدم المربّع من بوابة البيانات المفتوحة لدائرة الأراضي والأملاك، مع تقديرات بروبيرتك للإيجارات والعوائد."
                      : "Track registered sales and AED per sqft from the Dubai Land Department Open Data Gateway, alongside Propertic estimates for rents and yields."}
                  />
                  <BulletItem
                    icon={<LineChart className="w-4 h-4 text-propertic-gold" />}
                    title={isAR ? "مقارنة منطقتين جنباً إلى جنب" : "Side by side area comparison"}
                    body={isAR
                      ? "قارنوا أي منطقتين من ٤٤ منطقة في دبي: سعر القدم المربّع، العائد الإجمالي، المبيعات المسجّلة، واتجاهات الأسعار الشهرية."
                      : "Compare any two of 44 Dubai areas: price per sq ft, gross yield, registered sales, and monthly price trends."}
                  />
                  <BulletItem
                    icon={<Building2 className="w-4 h-4 text-propertic-gold" />}
                    title={isAR ? "محلّل الصفقات العقارية" : "Property deal analyzer"}
                    body={isAR
                      ? "أدخلوا سعر العقار والإيجار المتوقّع لتقدير التكاليف الكاملة والعائد ومقارنته ببيانات المنطقة."
                      : "Enter a property price and expected rent to estimate total costs and yield, benchmarked against area data."}
                  />
                  <BulletItem
                    icon={<Calculator className="w-4 h-4 text-propertic-gold" />}
                    title={isAR ? "حاسبة الرهن العقاري" : "Mortgage calculator"}
                    body={isAR
                      ? "قدّروا القسط الشهري التقريبي طوال فترة الرهن العقاري، بناءً على السعر ومدة القرض ونسبة الفائدة."
                      : "Estimate your approximate monthly commitment throughout the loan period, based on price, tenure, and interest rate."}
                  />
                </ul>
              </div>

              {/* PHASE 2, LIGHT WITH GOLD ACCENT */}
              <div className="rounded-[1.5rem] border-2 border-propertic-gold/40 bg-propertic-sand/40 p-6 sm:p-8 relative" data-testid="phase-2">
                <div className="inline-flex items-center gap-2 rounded-full border border-propertic-gold/50 bg-white/70 text-propertic-gold px-3 py-1 mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-propertic-gold" aria-hidden="true" />
                  <span className="font-mono-brand text-[10px] tracking-[0.2em] uppercase">
                    {isAR ? "المرحلة الثانية · الاستشارة المرخّصة" : "Phase 2, Licensed Advisory"}
                  </span>
                </div>
                <h3 className="font-editorial text-xl sm:text-2xl text-propertic-navy mb-3">
                  {isAR ? "إرشاد ودعم من مستشارين مرخّصين" : "Guidance and support by licensed advisors"}
                </h3>
                <p className="text-[14.5px] text-propertic-ink-2/90 leading-relaxed mb-6">
                  {isAR
                    ? "عندما تكونون جاهزين، يرافقكم مستشارونا المرخّصون في بروفست العقارية ذ.م.م (ريرا رقم ٤٣٩٥٠) باستشارة مخصّصة في كل خطوة من الصفقة:"
                    : "When you are ready, our licensed advisors at Provest Real Estate L.L.C (RERA 43950) guide you with dedicated advisory throughout the transaction:"}
                </p>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-5 text-[13.5px] text-propertic-ink leading-relaxed">
                  <BulletItem
                    icon={<Users className="w-4 h-4 text-propertic-gold" />}
                    title={isAR ? "تحديد المتطلّبات" : "Requirement mapping"}
                    body={isAR
                      ? "مطابقة أهدافكم الاستثمارية وميزانيتكم وملفّ المخاطر مع المخزون الفعّال في السوق."
                      : "Matching your investment goals, budget, and risk profile with active market inventory."}
                  />
                  <BulletItem
                    icon={<FileCheck className="w-4 h-4 text-propertic-gold" />}
                    title={isAR ? "دراسات حالة حقيقية" : "Real case studies"}
                    body={isAR
                      ? "مراجعة سجلّات صفقات فعلية وأرقام إعادة البيع التاريخية وتوقّعات التدفّق النقدي الفعلي."
                      : "Reviewing real transaction records, historical resale figures, and actual cash flow projections."}
                  />
                  <BulletItem
                    icon={<ShieldCheck className="w-4 h-4 text-propertic-gold" />}
                    title={isAR ? "تدقيق المطوّرين" : "Developer due diligence"}
                    body={isAR
                      ? "التحقّق من حسابات الضمان، وتقدّم الإنشاء، وسجلّات التسليم للمطوّر."
                      : "Verifying escrow account status, construction progress, and developer delivery records."}
                  />
                  <BulletItem
                    icon={<HandCoins className="w-4 h-4 text-propertic-gold" />}
                    title={isAR ? "مرافقة كاملة للمشتري" : "Full buyer support"}
                    body={isAR
                      ? "نُرافق المستثمرين خلال مراحل اتفاقية البيع، من الحجز حتى استلام المفاتيح."
                      : "We support investors through the Sales and Purchase Agreement journey, from booking to key handover."}
                  />
                </ul>
              </div>
            </div>
          </div>

          {/* ============ SECTION: COMPLIANCE ============ */}
          <div className="mt-20" data-testid="about-compliance">
            <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-3">
              {isAR ? "المصداقية والامتثال" : "Legitimacy, Compliance, Open Data"}
            </p>
            <h2 className="font-editorial text-2xl sm:text-3xl text-propertic-navy leading-tight">
              {isAR ? (
                <>الشفافية تتطلّب <span className="font-italic-serif text-propertic-gold">مساءلة.</span></>
              ) : (
                <>Transparency requires <span className="font-italic-serif text-propertic-gold">accountability.</span></>
              )}
            </h2>

            <div className="mt-8 grid md:grid-cols-3 gap-5">
              <CompliancePillar
                index="01"
                title={isAR ? "البيانات قبل حديث البيع" : "Data before sales talk"}
                body={isAR
                  ? "نعرض الأرقام أولاً، ولا نُروّج أبداً لعقارات لم تختاروها بأنفسكم."
                  : "We show the numbers first and never push stock you have not chosen yourself."}
                testid="pillar-independent"
              />
              <CompliancePillar
                index="02"
                title={isAR ? "مستشارون مرخّصون" : "Licensed advisors"}
                body={isAR
                  ? "نحن المستشارون أنفسهم: كل استشارة وصفقة يتولّاها فريقنا المرخّص في بروفست العقارية ذ.م.م، برقم تسجيل مكتب ساري المفعول من ريرا ورخصة تجارية من دائرة الاقتصاد والسياحة."
                  : "We are the advisors: every consultation and transaction is handled by our own licensed team at Provest Real Estate L.L.C, under a valid RERA Office Registration Number and DED commercial license."}
                testid="pillar-licensed"
              />
              <CompliancePillar
                index="03"
                title={isAR ? "دقّة بيانات تنظيمية" : "Regulatory data accuracy"}
                body={isAR
                  ? "بيانات المبيعات والأسعار من دائرة الأراضي والأملاك؛ أما الإيجارات والعوائد فهي تقديرات من بروبيرتك، وتُعرض المنهجية بجانب كل رقم."
                  : "Sales and price data from DLD; rents and yields are Propertic estimates with the method shown beside each figure."}
                testid="pillar-accuracy"
              />
            </div>
          </div>

          {/* ============ SECTION: COMPARISON ============ */}
          <div className="mt-20" data-testid="about-comparison">
            <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-3">
              {isAR ? "لماذا تبدؤون رحلتكم معنا" : "Why investors start with The Propertic"}
            </p>
            <h2 className="font-editorial text-2xl sm:text-3xl text-propertic-navy leading-tight mb-8">
              {isAR ? (
                <>الفرق بين قناة عقارية تقليدية <span className="font-italic-serif text-propertic-gold">ومنصّة بروبيرتك.</span></>
              ) : (
                <>Traditional real estate channels vs <span className="font-italic-serif text-propertic-gold">The Propertic platform.</span></>
              )}
            </h2>

            <div className="rounded-2xl border hairline bg-white/70 overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]" data-testid="comparison-table">
                <thead>
                  <tr className="bg-propertic-sand/60 text-propertic-muted">
                    <th className="text-start p-4 font-mono-brand text-[10px] tracking-[0.16em] uppercase w-1/2">
                      {isAR ? "قنوات العقار التقليدية" : "Traditional real estate channels"}
                    </th>
                    <th className="text-start p-4 font-mono-brand text-[10px] tracking-[0.16em] uppercase w-1/2 text-propertic-gold">
                      {isAR ? "منصّة بروبيرتك" : "The Propertic platform"}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y hairline">
                  {(isAR ? [
                    ["تُروِّج المخزون بناءً على حوافز عمولة المطوّر.", "تُوفّر بيانات مفتوحة تُتيح لكم التحقّق المستقل من القيم قبل الحجز."],
                    ["تُخفي رسوم الخدمة ورسوم دائرة الأراضي والعائد الصافي الحقيقي.", "محلّل الصفقات يعرض التكاليف الكاملة للشراء والعائد قبل أن تلتزموا."],
                    ["تشترط تقديم بيانات الاتصال قبل عرض الأرقام البسيطة.", "تمنحكم وصولاً مجانياً ومفتوحاً للأدوات والحاسبات التفاعلية."],
                    ["تتخلّى عن العميل فور دفع دفعة الحجز.", "مستشارونا المرخّصون من ريرا يرافقونكم بإرشاد مستمرّ."],
                  ] : [
                    ["Push inventory based on developer commission incentives.", "Provide open data so you verify values independently before booking."],
                    ["Hide service charges, DLD fees, and true net ROI.", "Our Deal Analyzer shows full purchase costs and yield before you commit."],
                    ["Require contact details before showing simple numbers.", "Give free, open access to every interactive tool and calculator."],
                    ["Hand off clients once the booking deposit is paid.", "Our RERA licensed advisors stay with you, offering continuous guidance."],
                  ]).map((row, i) => (
                    <tr key={i} className="hover:bg-propertic-sand/30 transition-colors" data-testid={`comparison-row-${i}`}>
                      <td className="p-4 text-propertic-ink-2/85 align-top">
                        <span className="inline-block me-2 font-mono-brand text-propertic-muted/70 text-[10px]">×</span>
                        {row[0]}
                      </td>
                      <td className="p-4 text-propertic-navy align-top">
                        <span className="inline-block me-2 font-mono-brand text-propertic-gold text-[10px]">✓</span>
                        {row[1]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ============ CTA ============ */}
          <div className="mt-16 rounded-2xl border hairline bg-propertic-sand/40 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5" data-testid="about-cta">
            <div>
              <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.22em] uppercase mb-2">
                {isAR ? "جاهزون للبدء؟" : "Ready to move to phase two?"}
              </p>
              <p className="text-[15px] text-propertic-ink leading-relaxed">
                {isAR
                  ? "بعد أن تحلّلوا الأرقام بأنفسكم، احجزوا استشارة مجانية مع مستشار بروفست المرخّص لتحويل التحليل إلى صفقة."
                  : "Once you have analyzed the numbers yourself, book a free consultation with a licensed Provest advisor to turn analysis into action."}
              </p>
            </div>
            <a
              href={lh("/#consultation")}
              className="inline-flex items-center gap-2 rounded-full h-12 px-6 bg-propertic-navy text-propertic-paper hover:bg-propertic-gold hover:text-propertic-navy font-mono-brand text-[11px] tracking-[0.18em] uppercase transition-colors shrink-0"
              data-testid="about-cta-button"
            >
              {isAR ? "احجزوا استشارة مجانية" : "Book a free consultation"}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

/* -------------------- helpers -------------------- */

function VerifiedCard({ icon, label, value, note, href, testid }) {
  const inner = (
    <div className="card-soft rounded-[1.5rem] p-6 hover:border-propertic-gold transition-colors h-full" data-testid={testid}>
      <div className="flex items-center gap-2.5 text-propertic-gold mb-4">
        {icon}
        <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase">{label}</p>
      </div>
      <p className="font-editorial text-lg text-propertic-navy leading-tight">{value}</p>
      <p className="mt-2 text-xs text-propertic-muted">{note}</p>
    </div>
  );
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" className="block">
      {inner}
    </a>
  ) : (
    inner
  );
}

function BulletItem({ icon, title, body }) {
  return (
    <li className="flex gap-3">
      <span className="shrink-0 mt-0.5" aria-hidden="true">{icon}</span>
      <span>
        <strong className="block font-editorial text-[14px] text-propertic-navy mb-0.5">{title}</strong>
        <span className="text-[13px] text-propertic-ink-2/85 leading-relaxed">{body}</span>
      </span>
    </li>
  );
}

function CompliancePillar({ index, title, body, testid }) {
  return (
    <div className="rounded-2xl border hairline bg-white/70 p-6" data-testid={testid}>
      <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.2em] mb-3 tabular">{index}</p>
      <h3 className="font-editorial text-lg text-propertic-navy leading-tight mb-2">{title}</h3>
      <p className="text-[13px] text-propertic-ink-2/85 leading-relaxed">{body}</p>
    </div>
  );
}
